import { useAuthContext } from "providers/Auth";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Plus } from "untitledui-js-base";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useQuery } from "@tanstack/react-query";
import { PropertyManagementSystem } from "shared/types";
import { instance } from "api/apiBase";
import { AddAppfolioPropertyModal } from "shared/app/AppfolioPropertiesModal";
import { useMemo, useState } from "react";

type SettingsPayload = {
  settings?: {
    name: string;
    readonly: boolean;
    value: string | null | boolean;
    title: string;
  }[];
};

const AddPropertyButton = ({
  onPropertyAdded,
  isAppfolioWithReports
}: {
  onPropertyAdded?: () => void;
  isAppfolioWithReports: boolean;
}) => {
  const [isAppfolioPropertiesModalOpen, setAppfolioPropertiesModalOpen] =
    useState(false);

  const dictionary = useUnitsPropertiesDictionary();
  const navigate = useNavigate();

  const handleModalSuccess = () => {
    // This will be called when a property is successfully added
    if (onPropertyAdded) onPropertyAdded();
  };

  if (!isAppfolioWithReports)
    return (
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={() => navigate("/properties/add")}
        Icon={Plus}
        className="w-full"
      >
        Add {dictionary.property}
      </Button>
    );

  return (
    <>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={() => {
          setAppfolioPropertiesModalOpen(true);
        }}
        Icon={Plus}
        className="w-full"
      >
        Appfolio {dictionary.properties}
      </Button>
      <AddAppfolioPropertyModal
        opened={isAppfolioPropertiesModalOpen}
        onClose={() => {
          setAppfolioPropertiesModalOpen(false);
        }}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

export const LayoutButtons = ({
  onPropertyAdded
}: {
  onPropertyAdded?: () => void;
}) => {
  const { partner, type, permissions } = useAuthContext();

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Fetch settings to check if Appfolio reports are enabled
  const { data: settingsData } = useQuery<SettingsPayload>({
    queryKey: ["SETTINGS"],
    queryFn: () => instance.get(`/portal/v1/settings`).then(({ data }) => data)
  });

  const isAppfolioWithReports = useMemo(() => {
    const isAppfolioPartner =
      partner?.property_management_system?.name ===
      PropertyManagementSystem.Appfolio;
    const value = settingsData?.settings?.find(
      (setting) => setting.name === "appfolio_use_reports"
    )?.value;

    const isEnabled = value === "true" || value === true;

    return Boolean(isAppfolioPartner && isEnabled);
  }, [partner, settingsData]);

  if (
    type.isBoomScreen &&
    (permissions.canManageProperties ||
      (partner?.property_management_system?.name ===
        PropertyManagementSystem.Appfolio &&
        isAppfolioWithReports))
  )
    return (
      <>
        {type.isBoomCRM ? (
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => handleCopyLink(partner?.listing_url ?? "")}
            className="w-full"
          >
            Copy listings link
          </Button>
        ) : (
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() =>
              handleCopyLink(partner?.magic_link?.application_link ?? "")
            }
            className="w-full"
          >
            Copy portfolio link
          </Button>
        )}
        <AddPropertyButton
          onPropertyAdded={onPropertyAdded}
          isAppfolioWithReports={isAppfolioWithReports}
        />
      </>
    );

  if (type.isBoomScreen)
    return (
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        onClick={() =>
          handleCopyLink(partner?.magic_link?.application_link ?? "")
        }
      >
        Copy portfolio link
      </Button>
    );

  if (permissions.canManageProperties)
    return (
      <AddPropertyButton
        onPropertyAdded={onPropertyAdded}
        // false is because this is not boomscreen
        isAppfolioWithReports={false}
      />
    );

  return null;
};
